import { useCallback, useEffect, useState } from "react";
import {
  listarInteresados,
  obtenerInteresado,
} from "../../../../services/interesadosService";
import { normalizeInteresado } from "../adapters/interesadosAdapter";
import { showToast } from "../../../../utils/showToast";

const initialFilters = { search: "", estado: "all", fuente: "all", fuenteLabel: "", empresa: "all" };

const initialResumen = {
  total: 0,
  nuevo: 0,
  interesado: 0,
  prematriculado: 0,
  matriculado: 0,
  descartado: 0,
  pendientes_coordinacion: 0,
};

export default function useInteresadosList() {
  const [interesadosPaginados, setInteresadosPaginados] = useState([]);
  const [seleccionado, setSeleccionado] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [filtros, setFiltros] = useState(initialFilters);
  const [searchDebounced, setSearchDebounced] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [resumen, setResumen] = useState(initialResumen);

  // Debounce del texto de busqueda para no disparar una consulta por cada tecla
  useEffect(() => {
    const timer = setTimeout(() => setSearchDebounced(filtros.search.trim()), 350);
    return () => clearTimeout(timer);
  }, [filtros.search]);

  useEffect(() => {
    setPage(1);
  }, [searchDebounced, filtros.estado, filtros.fuente, filtros.empresa]);

  const fetchInteresados = useCallback(async () => {
    setLoading(true);
    try {
      const response = await listarInteresados({
        pagina: page,
        porPagina: pageSize,
        search: searchDebounced || null,
        estado: filtros.estado,
        fuente_origen_id: filtros.fuente,
        empresa_origen_id: filtros.empresa,
      });
      const rows = Array.isArray(response?.data) ? response.data : [];
      setInteresadosPaginados(rows.map(normalizeInteresado));
      setTotal(Number(response?.total) || 0);
      setResumen({ ...initialResumen, ...(response?.resumen || {}) });
      setError(null);
      setLastUpdated(new Date());
    } catch (err) {
      console.error(err);
      const message = err.message || "No se pudo obtener los interesados.";
      setError(message);
      showToast({ type: "danger", message });
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, searchDebounced, filtros.estado, filtros.fuente, filtros.empresa]);

  useEffect(() => {
    fetchInteresados();
  }, [fetchInteresados]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const goToPage = useCallback(
    (nextPage) => {
      const safe = Math.min(totalPages, Math.max(1, nextPage));
      setPage(safe);
    },
    [totalPages]
  );

  const upsertInteresado = useCallback((interesado) => {
    if (!interesado?.id) return;
    setInteresadosPaginados((prev) => {
      const exists = prev.some((item) => String(item.id) === String(interesado.id));
      if (!exists) return prev;
      return prev.map((item) =>
        String(item.id) === String(interesado.id) ? normalizeInteresado({ ...item, ...interesado }) : item
      );
    });
    setLastUpdated(new Date());
  }, []);

  const removeInteresado = useCallback((id) => {
    setInteresadosPaginados((prev) => prev.filter((item) => String(item.id) !== String(id)));
    setLastUpdated(new Date());
  }, []);

  const seleccionar = useCallback(async (interesado) => {
    if (!interesado) {
      setSeleccionado(null);
      return;
    }
    const id = interesado.id ?? interesado;
    if (typeof interesado === "object") setSeleccionado(normalizeInteresado(interesado, id));
    try {
      const detalle = await obtenerInteresado(id);
      setSeleccionado(normalizeInteresado(detalle, id));
    } catch (err) {
      console.error(err);
      showToast({ type: "danger", message: "No se pudo cargar el interesado seleccionado." });
    }
  }, []);

  const limpiarFiltros = useCallback(() => setFiltros(initialFilters), []);

  return {
    interesadosPaginados,
    seleccionado,
    setSeleccionado,
    loading,
    error,
    resumen,
    filtros,
    setFiltros,
    limpiarFiltros,
    lastUpdated,
    page,
    pageSize,
    setPageSize,
    totalPages,
    total,
    goToPage,
    reload: fetchInteresados,
    seleccionar,
    upsertInteresado,
    removeInteresado,
  };
}

export { initialFilters as initialInteresadoFilters };
